import { NavLink } from "react-router-dom";
import { getUser } from "../../../config/auth";

function TacklLogoMark({ size = 34 }) {
  return (
    <img
      src="/logo.svg"
      alt="Tackl"
      style={{ width: size, height: size, objectFit: "contain", flexShrink: 0 }}
    />
  );
}

function Icon({ name }) {
  const s = { width: 16, height: 16, style: { flexShrink: 0 } };
  const p = { stroke: "currentColor", strokeWidth: 1.75, strokeLinecap: "round", strokeLinejoin: "round", fill: "none" };

  switch (name) {
    case "apps":
      return (
        <svg {...s} viewBox="0 0 24 24">
          <rect x="3" y="3" width="7" height="7" rx="1.5" {...p} />
          <rect x="14" y="3" width="7" height="7" rx="1.5" {...p} />
          <rect x="3" y="14" width="7" height="7" rx="1.5" {...p} />
          <rect x="14" y="14" width="7" height="7" rx="1.5" {...p} />
        </svg>
      );
    case "pipeline":
      return (
        <svg {...s} viewBox="0 0 24 24">
          <path d="M4 7h16" {...p} />
          <path d="M4 12h11" {...p} />
          <path d="M4 17h7" {...p} />
          <path d="m17 15 3 3 3-3" {...p} />
        </svg>
      );
    case "accounts":
      return (
        <svg {...s} viewBox="0 0 24 24">
          <rect x="3" y="4" width="18" height="16" rx="2" {...p} />
          <path d="M7 8h10" {...p} />
          <path d="M7 12h6" {...p} />
          <path d="M7 16h8" {...p} />
        </svg>
      );
    case "contacts":
      return (
        <svg {...s} viewBox="0 0 24 24">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" {...p} />
          <circle cx="9" cy="7" r="4" {...p} />
          <path d="M19 8v6" {...p} />
          <path d="M22 11h-6" {...p} />
        </svg>
      );
    case "activities":
      return (
        <svg {...s} viewBox="0 0 24 24">
          <path d="M21 12a9 9 0 1 1-3-6.7" {...p} />
          <path d="M21 3v6h-6" {...p} />
          <path d="M12 7v5l3 2" {...p} />
        </svg>
      );
    case "reports":
      return (
        <svg {...s} viewBox="0 0 24 24">
          <path d="M4 19V5" {...p} />
          <path d="M4 19h16" {...p} />
          <rect x="7" y="11" width="3" height="5" rx="1" {...p} />
          <rect x="12" y="8" width="3" height="8" rx="1" {...p} />
          <rect x="17" y="6" width="3" height="10" rx="1" {...p} />
        </svg>
      );
    default:
      return null;
  }
}

function RailLink({ to, label, icon, onClick }) {
  return (
    <NavLink
      to={to}
      title={label}
      aria-label={label}
      className={({ isActive }) => `sidebar-rail-item${isActive ? " active" : ""}`}
      onClick={onClick}
    >
      <Icon name={icon} />
    </NavLink>
  );
}

function SidebarLink({ to, label, icon, disabled, onClick }) {
  if (disabled) {
    return (
      <button type="button" className="sidebar-item disabled" disabled aria-disabled="true">
        <Icon name={icon} />
        <span>{label}</span>
      </button>
    );
  }

  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) => `sidebar-item${isActive ? " active" : ""}`}
      onClick={onClick}
    >
      <Icon name={icon} />
      <span>{label}</span>
    </NavLink>
  );
}

function SidebarSection({ label }) {
  return (
    <div className="sidebar-section-row">
      <span className="sidebar-section">{label}</span>
    </div>
  );
}

export default function CrmSidebar({ isOpen, onClose }) {
  const user = getUser();
  const role = user?.role || "Staff";
  const initial = (user?.name || user?.email || "?")[0].toUpperCase();

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      <aside className={`sidebar crm-sidebar${isOpen ? " sidebar--open" : ""}`}>
        <div className="sidebar-primary-rail" aria-label="CRM primary navigation">
          <div className="sidebar-rail-logo">
            <TacklLogoMark size={30} />
          </div>
          <nav className="sidebar-rail-nav">
            <RailLink to="/crm/pipeline" label="CRM Pipeline" icon="pipeline" onClick={onClose} />
          </nav>
        </div>

        <div className="sidebar-panel">
          <div className="sidebar-header">
            <div className="sidebar-brand">
              <div>
                <div className="sidebar-title">CRM Workspace</div>
                <div className="sidebar-subtitle">Sales pipeline</div>
              </div>
            </div>
            <button className="sidebar-close-btn" onClick={onClose} aria-label="Close menu">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <nav className="sidebar-nav">
            <SidebarSection label="CRM" />
            <SidebarLink to="/crm/pipeline" label="Pipeline" icon="pipeline" onClick={onClose} />

            <SidebarSection label="Sales Data" />
            <SidebarLink to="/crm/accounts" label="Accounts" icon="accounts" onClick={onClose} />
            <SidebarLink to="/crm/contacts" label="Contacts" icon="contacts" onClick={onClose} />
            <SidebarLink to="/crm/activities" label="Activities" icon="activities" onClick={onClose} />

            <SidebarSection label="Insights" />
            <SidebarLink to="/crm/reports" label="CRM Reports" icon="reports" onClick={onClose} />
          </nav>

          <div className="sidebar-footer">
            <div className="sidebar-user-row">
              <div className="sidebar-user-avatar">{initial}</div>
              <div className="sidebar-user-info">
                <div className="sidebar-user-name">
                  {user?.name || user?.email?.split("@")[0] || "User"}
                </div>
                <div className="sidebar-user-role">{role}</div>
              </div>
              <span className="sidebar-beta-badge">CRM</span>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
